import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import UseAxiosPublic from '../hooks/UseAxiosPublic';

const SignUp = () => {
    // 1. Changed "avatar" to "profileImage" everywhere
    const { register, handleSubmit, formState: { errors } } = useForm();
    const [imagePreview, setImagePreview] = useState(null);
    const axiosPublic = UseAxiosPublic();

    const onSubmit = async (data) => {
        console.log(data);
        try {
            const formData = new FormData();
            Object.entries(data).forEach(([key, value]) => {
                if (key === "profileImage") {
                    if (value && value.length > 0) {
                        formData.append(key, value[0]); // Correctly appends the File object
                    }
                } else {
                    formData.append(key, value);
                }
            });

            // Note: Axios automatically sets the multipart header and boundary when passing FormData, 
            // but keeping your explicit header as is since it works fine.
            const res = await axiosPublic.post("/signup", formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            console.log(res.data);
        } catch (error) {
            console.error("Error during sign up:", error);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setImagePreview(null);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 flex flex-col justify-center items-center p-4 antialiased">
            <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-6">

                <div className="text-center space-y-2">
                    <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
                        Create Account
                    </h1>
                    <p className="text-sm text-slate-400">Join us today. It only takes a minute.</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    {/* Full Name Field */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-slate-300 tracking-wide uppercase px-1">Full Name</label>
                        <input
                            type="text"
                            placeholder="John Doe"
                            className={`w-full bg-slate-950 border text-slate-100 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:ring-2 transition-all ${errors.name ? 'border-rose-500/50 focus:ring-rose-500/20 focus:border-rose-500' : 'border-slate-800 focus:ring-indigo-500/20 focus:border-indigo-500'}`}
                            {...register('name', { required: 'Name is required' })}
                        />
                        {errors.fullName && <p className="text-xs text-rose-400 px-1">{errors.fullName.message}</p>}
                    </div>

                    {/* Email Field */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-slate-300 tracking-wide uppercase px-1">Email Address</label>
                        <input
                            type="email"
                            placeholder="you@example.com"
                            className={`w-full bg-slate-950 border text-slate-100 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:ring-2 transition-all ${errors.email ? 'border-rose-500/50 focus:ring-rose-500/20 focus:border-rose-500' : 'border-slate-800 focus:ring-indigo-500/20 focus:border-indigo-500'}`}
                            {...register('email', {
                                required: 'Email is required',
                                pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' }
                            })}
                        />
                        {errors.email && <p className="text-xs text-rose-400 px-1">{errors.email.message}</p>}
                    </div>

                    {/* Password Field */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-slate-300 tracking-wide uppercase px-1">Password</label>
                        <input
                            type="password"
                            placeholder="••••••••"
                            className={`w-full bg-slate-950 border text-slate-100 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:ring-2 transition-all ${errors.password ? 'border-rose-500/50 focus:ring-rose-500/20 focus:border-rose-500' : 'border-slate-800 focus:ring-indigo-500/20 focus:border-indigo-500'}`}
                            {...register('password', {
                                required: 'Password is required',
                                minLength: { value: 6, message: 'Must be at least 6 characters' }
                            })}
                        />
                        {errors.password && <p className="text-xs text-rose-400 px-1">{errors.password.message}</p>}
                    </div>

                    {/* Profile Picture Field */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-slate-300 tracking-wide uppercase px-1">Profile Picture</label>
                        {imagePreview ? (
                            <div className="relative flex items-center justify-center w-full">
                                <img src={imagePreview} alt="Preview" className="w-full h-28 object-cover rounded-xl border border-slate-800" />
                            </div>
                        ) : (
                            <div className="relative flex items-center justify-center w-full">
                                {/* 2. Updated errors.avatar to errors.profileImage below */}
                                <label className={`flex flex-col items-center justify-center w-full h-28 border border-dashed rounded-xl cursor-pointer bg-slate-950/50 hover:bg-slate-950 transition-all ${errors.profileImage ? 'border-rose-500/50' : 'border-slate-800 hover:border-slate-700'}`}>
                                    <div className="flex flex-col items-center justify-center pt-4 pb-4 px-2 text-center">
                                        <svg className="w-6 h-6 mb-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                                        </svg>
                                        <p className="text-xs text-slate-300 font-medium">Click to upload image</p>
                                        <p className="text-[10px] text-slate-500 mt-0.5">PNG or JPG (Max 5MB)</p>
                                    </div>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        {...register('profileImage', { required: 'Please upload a profile picture' })} // 3. Changed name to 'profileImage'
                                        onChange={(e) => {
                                            register('profileImage').onChange(e); // Let React Hook Form track the change
                                            handleImageChange(e); // Handle your preview logic
                                        }}
                                    />
                                </label>
                            </div>
                        )}
                        {/* 4. Updated validation error pointer */}
                        {errors.profileImage && <p className="text-xs text-rose-400 px-1">{errors.profileImage.message}</p>}
                    </div>

                    <button type="submit" className="w-full mt-2 bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700 text-white font-medium rounded-xl py-3.5 text-sm shadow-lg shadow-indigo-500/20 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 active:scale-[0.98] transition-all">
                        Sign Up
                    </button>
                </form>

                <div className="text-center pt-2">
                    <p className="text-xs text-slate-400">
                        Already have an account?{' '}
                        <a href="#login" className="text-indigo-400 hover:text-indigo-300 font-medium transition-colors">Sign In</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SignUp;